<template>
  <div class="relative w-full" ref="wrapRef">
    <Input 
      :id="'autoComplete-'+id" 
      :label="label" 
      :placeholder="placeholder" 
      :warningMessage="warningMessage" 
      :modelValue="innerValue"
      @update:modelValue="onInput" 
      @focus="onFocus" 
      @blur="onBlur" 
      @keydown="onKeydown" 
    />

    <teleport to="body">
      <ul 
        v-if="showList" 
        class="auto_complete dropdown-portal" 
        :class="{ 'no-border': isEmptyList }" 
        :style="menuStyle"
        ref="listRef" 
        @scroll="onScroll"
      >
        <li 
          v-for="(item, idx) in itemsView" 
          :key="idx" 
          @mousedown.prevent="pick(item)"
          :class="{ active: idx === activeIndex }"
        >
          {{ displayText(item) }}
        </li>

        <li v-if="!loading && !loadingMore && isEmpty && !hideNotFoundText" class="empty">
          {{ notFoundText }}
        </li>

        <li v-if="loadingMore" class="spinner">
          <span class="loader"></span>
        </li>
      </ul>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  id: { type: String, default: "" },
  modelValue: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  warningMessage: { type: String, default: '' },
  fetcher: { type: Function, required: true },
  extraParams: { type: Object, default: () => ({}) },
  type: { type: [Number, String], default: null },
  itemText: { type: [String, Function], default: 'name' },
  itemValue: { type: [String, Function], default: (v) => (typeof v === 'string' ? v : v?.name ?? '') },
  debounce: { type: Number, default: 300 },
  pageSize: { type: Number, default: 10 },
  hideNotFoundText: { type: Boolean, default: false },
  notFoundText: { type: String, default: 'ไม่พบข้อมูล' },
  fetchOnFocusWhenEmpty: { type: Boolean, default: true },
  fetchWhenEmpty: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue', 'select'])

const wrapRef = ref(null)
const listRef = ref(null)
const innerValue = ref(props.modelValue ?? '')
const showList = ref(false)
const items = ref([])
const offset = ref(0)
const lastBatchCount = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const activeIndex = ref(-1)
const initialized = ref(false) 

const itemsView = computed(() => Array.isArray(items.value) ? items.value : [])
const isEmpty = computed(() => itemsView.value.length === 0)

watch(() => props.modelValue, v => { innerValue.value = v ?? '' })

const displayText = (item) => {
  if (typeof props.itemText === 'function') return props.itemText(item)
  if (typeof item === 'string') return item
  return item?.[props.itemText] ?? ''
}

const getValue = (item) => {
  if (typeof props.itemValue === 'function') return props.itemValue(item)
  if (typeof item === 'string') return item
  return item?.[props.itemValue] ?? ''
}

function makeDebounce(fn, delay) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), delay)
  }
}

const debouncedLoad = makeDebounce(() => {
  offset.value = 0
  lastBatchCount.value = 0
  load(false, false)
}, props.debounce)

function onInput(v) {
  innerValue.value = v
  emit('update:modelValue', v)
  showList.value = true
  if ((v ?? '') === '' && props.fetchWhenEmpty) {
    offset.value = 0
    lastBatchCount.value = 0
    load(false, false)
  } else {
    debouncedLoad()
  }
}

function onFocus() {
  showList.value = true
  offset.value = 0
  lastBatchCount.value = 0
  if ((innerValue.value ?? '') === '' && !props.fetchOnFocusWhenEmpty) return
  load(false, false)
}

function onBlur() {
  setTimeout(() => { showList.value = false }, 150)
}

function ensureVisible() {
  const el = listRef.value
  const idx = activeIndex.value
  const kids = el?.children
  if (!kids || idx < 0 || idx >= kids.length) return
  kids[idx]?.scrollIntoView?.({ block: 'nearest' })
}

function onKeydown(e) {
  const len = itemsView.value.length
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      if (!showList.value) onFocus()
      if (len) {
        activeIndex.value = (activeIndex.value + 1) % len
        ensureVisible()
      }
      break
    case 'ArrowUp':
      e.preventDefault()
      if (len) {
        activeIndex.value = (activeIndex.value - 1 + len) % len
        ensureVisible()
      }
      break
    case 'Enter':
      if (!showList.value) return
      e.preventDefault()
      if (activeIndex.value >= 0 && activeIndex.value < len) {
        pick(itemsView.value[activeIndex.value])
      }
      break
    case 'Escape':
      e.preventDefault()
      showList.value = false
      break
  }
}

function pick(item) {
  const val = getValue(item)
  emit('update:modelValue', val)
  emit('select', item)
  showList.value = false
  items.value = []
}

async function load(append = false, isLoadMore = false) {
  if (loading.value) return
  loading.value = true
  if (isLoadMore) loadingMore.value = true

  try {
    const list = await props.fetcher({
      query: String(innerValue.value ?? ''),
      type: props.type,
      offset: offset.value,
      ...props.extraParams,
    })
    const arr = Array.isArray(list) ? list : []
    lastBatchCount.value = arr.length
    if (append) items.value.push(...arr)
    else items.value = arr
    if (!append) activeIndex.value = items.value.length ? 0 : -1
    initialized.value = true 
  } finally {
    loading.value = false
    loadingMore.value = false
    await nextTick()
    placeDropdown()
  }
}

function onScroll(e) {
  const el = e.target
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
  if (nearBottom && lastBatchCount.value === 100 && !loading.value) {
    offset.value += 100
    load(true, true)
  }
}

/* ---- Positioning Logic (iOS Fixed) ---- */
const menuStyle = ref({})
function placeDropdown() {
  const host = wrapRef.value
  if (!host || !showList.value) return
  
  const rect = host.getBoundingClientRect()
  
  // คำนวณ Scroll ปัจจุบันของหน้าจอ
  const scrollY = window.pageYOffset || document.documentElement.scrollTop
  const scrollX = window.pageXOffset || document.documentElement.scrollLeft

  menuStyle.value = {
    position: 'absolute', // ใช้ absolute แทน fixed เพื่อแก้ปัญหาคีย์บอร์ด iOS
    left: `${rect.left + scrollX}px`,
    top: `${rect.bottom + scrollY}px`,
    width: `${rect.width}px`,
    maxHeight: '130px',
    zIndex: 999999
  }
}

function repositionAll() {
  if (showList.value) placeDropdown()
}

const isEmptyList = computed(() => {
  if (!initialized.value) return true
  return itemsView.value.length === 0 && !loading.value && !loadingMore.value && props.hideNotFoundText
})

onMounted(() => {
  window.addEventListener('resize', repositionAll)
  window.addEventListener('scroll', repositionAll, true)
  
  // สำคัญ: รองรับการขยับของคีย์บอร์ดบน iOS Safari
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', repositionAll)
    window.visualViewport.addEventListener('scroll', repositionAll)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', repositionAll)
  window.removeEventListener('scroll', repositionAll, true)
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', repositionAll)
    window.visualViewport.removeEventListener('scroll', repositionAll)
  }
})
</script>

<style scoped lang="scss">
.auto_complete {
  width: 100%;
  border: 1px solid transparent;
  border-radius: 4px;
  overflow-y: auto;
  background: #fff;
  margin: 0;
  padding: 0;
  list-style: none;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);

  li {
    padding: 8px 12px;
    cursor: pointer;
    transition: background .2s;
  }

  li:hover, li.active {
    background: #FBEFF8;
  }

  .spinner {
    display: flex;
    justify-content: center;
    padding: 8px;
    pointer-events: none;
  }

  .empty {
    padding: 8px 12px;
    color: #6b7280;
    font-size: 0.9rem;
  }
}

.dropdown-portal {
  /* ลบพิกัดตายตัวออก ให้ใช้ค่าจาก menuStyle (JS) แทน */
  box-sizing: border-box;
}

.loader {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: #9b237b;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.auto_complete.no-border {
  border-color: transparent !important;
  box-shadow: none;
}

.auto_complete:not(.no-border) {
  border-color: #b7b2b5;
}
</style>